import { format, isToday } from 'date-fns';
import type { DayAgendaItem } from '@/lib/calendarAgenda';
import { PanelToggleIcon } from '@/components/calendar/shared/CalendarPageIcons';
import { CALENDAR_KIND_CHIP_CLASS } from '@/components/calendar/shared/calendarTheme';
import type { HrmsCurrentAttendanceStatus } from '@/api/hrms';

type Props = {
  selectedDate: Date | null;
  selectedAgenda: DayAgendaItem[] | null;
  attendanceToday?: HrmsCurrentAttendanceStatus | null;
  attendanceByDay?: Record<string, 'present' | 'absent' | 'weekoff' | 'holiday'> | null;
  onHide: () => void;
  onOpenItem: (item: DayAgendaItem) => void;
};

export function CalendarAgendaAside({ selectedDate, selectedAgenda, attendanceToday, attendanceByDay, onHide, onOpenItem }: Props) {
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const selectedIsToday = selectedDate ? isToday(selectedDate) : false;
  const selectedDateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const selectedIsFuture = selectedDateKey ? selectedDateKey > todayKey : false;

  let attendanceLabel: string | null = null;
  let workedSeconds: number | null = null;

  if (selectedIsFuture) {
    // Future — no attendance data exists yet, show nothing.
  } else if (selectedIsToday && attendanceToday != null) {
    // Today — use live current-status (has workedSeconds, real-time clocked-in state).
    attendanceLabel = attendanceToday.presentToday || attendanceToday.isClockedIn ? 'Present' : 'Absent';
    workedSeconds = attendanceToday.workedSecondsToday ?? null;
  } else if (!selectedIsToday && selectedDateKey && attendanceByDay) {
    // Past date — look up the monthly report map.
    // Only show if a real record exists in the map (key is present).
    const status = attendanceByDay[selectedDateKey];
    if (status === 'present') attendanceLabel = 'Present';
    else if (status === 'absent') attendanceLabel = 'Absent';
    else if (status === 'weekoff') attendanceLabel = 'Week Off';
    else if (status === 'holiday') attendanceLabel = 'Holiday';
  }

  return (
    <aside className="w-full shrink-0 xl:w-[300px] 2xl:w-[360px]">
      <div className="rounded-3xl border border-slate-100 bg-white p-4 sm:p-6 xl:sticky xl:top-0 dark:border-white/5 dark:bg-card">
        <button type="button" onClick={onHide} className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-500 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10" aria-label="Hide agenda panel" title="Hide agenda panel">
          <PanelToggleIcon collapsed={false} />
        </button>
        <h3 className="mb-1 text-xl font-bold tracking-tight text-[#0f172b] dark:text-white">
          {selectedDate ? format(selectedDate, 'EEEE, MMM d') : 'Select a day'}
        </h3>
        <p className="mb-6 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Your Agenda</p>

        {attendanceLabel ? (
          <div className="mb-5 rounded-2xl bg-[#F6F7FB] p-4 dark:bg-white/5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  {selectedIsToday ? 'Attendance (Today)' : 'Attendance'}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0f172b] dark:text-white">{attendanceLabel}</p>
              </div>
              <span
                className={[
                  'inline-flex h-3 w-3 rounded-full ring-4',
                  attendanceLabel === 'Present' ? 'bg-emerald-500 ring-emerald-500/15'
                  : attendanceLabel === 'Week Off' || attendanceLabel === 'Holiday' ? 'bg-amber-400 ring-amber-400/15'
                  : 'bg-rose-500 ring-rose-500/15',
                ].join(' ')}
                aria-label={attendanceLabel}
                title={attendanceLabel}
              />
            </div>
            {workedSeconds != null ? (
              <p className="mt-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                Worked: {Math.floor(workedSeconds / 3600)}h {Math.floor((workedSeconds % 3600) / 60)}m
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="custom-scrollbar max-h-[calc(100vh-320px)] space-y-4 overflow-y-auto pr-2">
          {selectedAgenda && selectedAgenda.length ? (
            selectedAgenda.map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpenItem(item)}
                className="group relative flex cursor-pointer flex-col gap-2 rounded-2xl border border-transparent bg-[#F6F7FB] p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#5B6CF6]/40 hover:bg-white hover:shadow-md dark:bg-white/5 dark:hover:bg-white/10"
              >
                <div className="flex items-center justify-between">
                  <span className={[
                    'rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide',
                    item.kind === 'event' ? CALENDAR_KIND_CHIP_CLASS.event
                    : item.kind === 'meeting' ? CALENDAR_KIND_CHIP_CLASS.meeting
                    : item.kind === 'org-event' ? CALENDAR_KIND_CHIP_CLASS['org-event']
                    : item.kind === 'holiday' ? CALENDAR_KIND_CHIP_CLASS.holiday
                    : item.kind === 'task' ? CALENDAR_KIND_CHIP_CLASS.task
                    : item.kind === 'note' ? CALENDAR_KIND_CHIP_CLASS.note
                    : item.kind === 'pms-task' ? CALENDAR_KIND_CHIP_CLASS['pms-task']
                    : item.kind === 'pms-project' ? CALENDAR_KIND_CHIP_CLASS['pms-project']
                    : CALENDAR_KIND_CHIP_CLASS.schedule,
                  ].join(' ')}>
                    {item.kind.replace('-', ' ')}
                  </span>
                  <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{item.timeLabel}</span>
                </div>
                <p className="text-sm font-semibold text-[#0f172b] transition-colors group-hover:text-[#5B47E8] dark:text-white dark:group-hover:text-[#8B7CFF]">{item.title}</p>
                {item.details ? <p className="line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{item.details}</p> : null}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-[#5B6CF6]/30 bg-[#F6F7FB]/60 px-4 py-12 text-center dark:border-[#8B7CFF]/30 dark:bg-white/[0.02]">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF2FF] dark:bg-[#1C1F3F]">
                <svg className="h-6 w-6 text-[#5B47E8] dark:text-[#8B7CFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                {selectedIsToday ? 'No items planned for today' : selectedIsFuture ? 'Nothing scheduled yet' : 'No items planned for this day'}
              </p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
